import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import io from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface PendingMatch {
  id: string;
  mode: string;
  startTime: string;
  participants: Array<{
    id: string;
    side: string;
    team?: {
      name: string;
      id?: string;
    };
  }>;
  playSession: {
    device: {
      venue: {
        name: string;
      };
    };
  };
}

const JoinSession = () => {
  const navigate = useNavigate();
  const { sessionCode: sessionCodeParam } = useParams<{ sessionCode?: string }>();
  const [searchParams] = useSearchParams();
  const [sessionJoinCode, setSessionJoinCode] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [pendingMatches, setPendingMatches] = useState<PendingMatch[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [acceptingMatch, setAcceptingMatch] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [autoJoinTriggered, setAutoJoinTriggered] = useState(false);
  const [myTeams, setMyTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedTeams, setSelectedTeams] = useState<Record<string, string>>({});

  const prefilledCode = (sessionCodeParam || searchParams.get('sessionCode') || searchParams.get('code') || '').toUpperCase();

  const fetchPendingMatches = useCallback(async () => {
    try {
      const response = await api.get('/matches/pending');
      setPendingMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch pending matches:', error);
    } finally {
      setLoadingMatches(false);
    }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await api.get('/teams/mine');
      setMyTeams(res.data || []);
    } catch (error) {
      console.error('Failed to fetch user teams:', error);
    }
  }, []);

  useEffect(() => {
    const tryResumeFromLocal = async () => {
      const storedMatchId = localStorage.getItem('activeMatchId');
      if (!storedMatchId) {
        return false;
      }

      try {
        const match = await api.get(`/matches/${storedMatchId}`);
        if (match.data && match.data.status !== 'finished') {
          navigate(`/matches/${storedMatchId}`, { replace: true });
          return true;
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          console.error('Failed to resume stored match', err);
        }
      }

      localStorage.removeItem('activeMatchId');
      return false;
    };

    const validateExistingProgress = async () => {
      const resumed = await tryResumeFromLocal();
      if (resumed) {
        return;
      }

      try {
        const activeMatch = await api.get('/matches/active');
        if (activeMatch.data) {
          navigate(`/matches/${activeMatch.data.id}`, { replace: true });
          return;
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          console.error('Failed to check active match', err);
        }
      }

      try {
        const activeSession = await api.get('/sessions/active');
        if (activeSession.data) {
          navigate('/create-match', { state: { session: activeSession.data }, replace: true });
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          console.error('Failed to check active session', err);
        }
      }
    };

    validateExistingProgress();
  }, [navigate]);

  useEffect(() => {
    if (prefilledCode) {
      setSessionJoinCode(prefilledCode);
    }
  }, [prefilledCode]);

  useEffect(() => {
    fetchPendingMatches();
    fetchTeams();
  }, [fetchPendingMatches, fetchTeams]);

  useEffect(() => {
    const interval = setInterval(fetchPendingMatches, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingMatches]);

  const handleJoin = useCallback(async (code: string) => {
    if (!code) return;
    setError('');
    setSuccess('');
    setJoining(true);
    try {
      const normalizedCode = code.toUpperCase();
      const response = await api.post('/sessions/join', { sessionJoinCode: normalizedCode });
      console.log(response.data);
      navigate('/create-match', { state: { session: response.data } });
    } catch (error: any) {
      console.error(error);
      setError(error.response?.data?.error || 'Nelze se připojit k sezení. Zkontrolujte kód a zkuste to znovu.');
    } finally {
      setJoining(false);
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleJoin(sessionJoinCode);
  };

  useEffect(() => {
    if (prefilledCode && !autoJoinTriggered) {
      setAutoJoinTriggered(true);
      handleJoin(prefilledCode);
    }
  }, [prefilledCode, autoJoinTriggered, handleJoin]);

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket'] });
    socket.on('pendingMatchCreated', fetchPendingMatches);
    socket.on('pendingMatchAccepted', fetchPendingMatches);
    socket.on('pendingMatchRejected', fetchPendingMatches);

    return () => {
      socket.off('pendingMatchCreated', fetchPendingMatches);
      socket.off('pendingMatchAccepted', fetchPendingMatches);
      socket.off('pendingMatchRejected', fetchPendingMatches);
      socket.disconnect();
    };
  }, [fetchPendingMatches]);

  const handleAcceptMatch = async (matchId: string, teamId?: string) => {
    setAcceptingMatch(matchId);
    try {
      await api.post(`/matches/${matchId}/accept`, teamId ? { teamId } : undefined);
      navigate(`/matches/${matchId}`);
    } catch (error: any) {
      console.error('Failed to accept match:', error);
      alert('Nepodařilo se přijmout zápas');
      setAcceptingMatch(null);
    }
  };

  const handleRejectMatch = async (matchId: string) => {
    if (!window.confirm('Opravdu chcete odmítnout tento zápas?')) {
      return;
    }
    try {
      await api.post(`/matches/${matchId}/reject`);
      setPendingMatches(pendingMatches.filter(m => m.id !== matchId));
    } catch (error: any) {
      console.error('Failed to reject match:', error);
      alert('Nepodařilo se odmítnout zápas');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 animate-fade-in">
          <div className="text-6xl sm:text-7xl mb-4 animate-bounce-slow">🎮</div>
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Hrát
          </h2>
          <p className="text-gray-600 text-lg">Připojte se k hře nebo přijměte výzvu</p>
        </div>

        {/* Pending Match Invitations */}
        {!loadingMatches && pendingMatches.length > 0 && (
          <div className="mb-8 animate-slide-up">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-400 p-1 rounded-3xl shadow-card">
              <div className="bg-white rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-3xl">⚡</div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Výzvy k zápasům
                  </h3>
                </div>
                <div className="space-y-4">
                  {pendingMatches.map((match) => {
                    const hostParticipant = match.participants.find(p => p.side === 'left');
                    const hostTeam = hostParticipant?.team?.name || 'Tým';
                    const rightParticipant = match.participants.find(p => p.side === 'right');
                    const invitedTeam = rightParticipant?.team?.name || 'Vyberte svůj tým';
                    const requiresSelection = !rightParticipant;
                    const selectedTeamId = selectedTeams[match.id];

                    return (
                      <div key={match.id} className="bg-gradient-to-r from-primary-50 to-secondary-50 p-4 rounded-2xl">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg font-bold text-gray-900">{hostTeam}</span>
                              <span className="text-gray-500">vs</span>
                              <span className="text-lg font-bold text-gray-900">{invitedTeam}</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              📍 {match.playSession.device.venue.name}
                            </div>
                          </div>
                          <div className="flex gap-2 sm:flex-col sm:w-auto w-full">
                            {requiresSelection ? (
                              <select
                                value={selectedTeamId || ''}
                                onChange={(e) => setSelectedTeams({ ...selectedTeams, [match.id]: e.target.value })}
                                className="flex-1 sm:flex-none px-4 py-2 bg-white rounded-xl border-2 border-gray-200 focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all"
                              >
                                <option value="">Vyberte svůj tým...</option>
                                {myTeams.map((team) => (
                                  <option key={team.id} value={team.id}>{team.name}</option>
                                ))}
                              </select>
                            ) : (
                              <div className="flex-1 sm:flex-none px-4 py-2 bg-white rounded-xl border-2 border-gray-200 text-sm text-gray-700">
                                Pozvánka pro tým: <span className="font-bold">{invitedTeam}</span>
                              </div>
                            )}
                            <div className="flex gap-2 w-full sm:w-auto sm:flex-col">
                              <button
                                onClick={() => handleAcceptMatch(match.id, requiresSelection ? selectedTeamId : undefined)}
                                disabled={acceptingMatch === match.id || (requiresSelection && !selectedTeamId)}
                                className="flex-1 sm:flex-none px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                              >
                                {acceptingMatch === match.id ? 'Přijímám...' : 'Přijmout ✓'}
                              </button>
                              <button
                                onClick={() => handleRejectMatch(match.id)}
                                disabled={acceptingMatch === match.id}
                                className="flex-1 sm:flex-none px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl transition-all duration-200 disabled:opacity-50"
                              >
                                Odmítnout ✗
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 text-center text-green-700 bg-green-50 rounded-2xl border border-green-200 animate-slide-up">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 text-center text-red-700 bg-red-50 rounded-2xl border border-red-200 animate-slide-up">
            <span className="mr-2">⚠️</span>
            {error}
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-10 rounded-3xl shadow-card-hover border border-white mb-8 animate-slide-up">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3 text-center sm:text-left">
                🔑 Zadejte kód sezení
              </label>
              <input
                type="text"
                required
                maxLength={6}
                className="w-full p-5 text-gray-900 text-xl sm:text-2xl font-bold text-center bg-white rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all duration-300 uppercase tracking-widest"
                placeholder="ABC123"
                value={sessionJoinCode}
                onChange={(e) => setSessionJoinCode(e.target.value.toUpperCase())}
              />
              <p className="text-xs text-gray-500 text-center mt-2">
                6 znaků zobrazených na displeji zařízení
              </p>
            </div>
            <button
              type="submit"
              disabled={!sessionJoinCode || joining}
              className="w-full py-5 font-bold text-white text-xl bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl shadow-lg hover:shadow-glow hover:scale-105 transform transition-all duration-300 disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed"
            >
              {joining ? 'Připojuji...' : 'Připojit se 🚀'}
            </button>
          </form>
        </div>

        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h4 className="font-bold text-gray-900 mb-3 text-lg">💡 Jak se připojit</h4>
          <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
            <li className="flex items-start">
              <span className="mr-2">1️⃣</span>
              <span>Přejděte ke stolnímu fotbálku a vytvořte si na něm kód pro hru</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">2️⃣</span>
              <span>Kód ze 6 znaků najdete na obrazovce zařízení</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">3️⃣</span>
              <span>Zadejte kód do pole výše a připojte se</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">4️⃣</span>
              <span>Začněte hrát a sledujte skóre v reálném čase!</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JoinSession;
